import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteAssetImageMutation, useReplaceFeaturedImageMutation, useUploadAssetImagesMutation } from '../../../api/asset/assetApiSlice';
import AssetDetailSection from './AssetDetailSection';
import { showsCoverMissingTile } from '../objectCover';

interface ImagesSectionProps {
    featuredImage: string | null | undefined;
    additionalImages: string[];
    maxImages?: number;
    mode: 'view' | 'edit' | 'create';
    assetId?: string;
    // Deferred upload props — when provided, image changes are staged and only
    // persisted when the parent calls the mutations (i.e. on Save).
    pendingFeaturedImage?: File | null;
    pendingAdditionalImages?: File[];
    pendingDeletions?: string[];
    onFeaturedImageSelect?: (file: File) => void;
    onAdditionalImagesAdd?: (files: File[]) => void;
    onImageDelete?: (url: string) => void;
    onPendingAdditionalDelete?: (index: number) => void;
    isUploadingImages?: boolean;
    isReplacingFeaturedExternal?: boolean;
    /**
     * The object has no cover by the display rule (`objectCover`): view mode
     * then shows an empty cover tile reading "Cover photo: missing". In edit
     * mode the add-cover tile takes its place.
     */
    coverMissing?: boolean;
}

/** The empty cover tile marking a missing cover (PO, 2026-11-03). */
const CoverMissingTile: React.FC<{ show: boolean }> = ({ show }) => {
    const { t } = useTranslation();
    if (!show) return null;
    return (
        <div
            className={'aspect-square rounded-lg border-2 border-dashed border-foreground/20'
                + ' flex items-center justify-center px-2 text-center'}
        >
            <span className={'text-xs text-foreground/40'}>{t('asset_detail.images.cover_missing')}</span>
        </div>
    );
};


const ImagesSection: React.FC<ImagesSectionProps> = ({
    featuredImage,
    additionalImages,
    maxImages,
    mode,
    assetId,
    pendingFeaturedImage,
    pendingAdditionalImages,
    pendingDeletions,
    onFeaturedImageSelect,
    onAdditionalImagesAdd,
    onImageDelete,
    onPendingAdditionalDelete,
    isUploadingImages,
    isReplacingFeaturedExternal,
    coverMissing = false,
}) => {
    const { t } = useTranslation();
    // File inputs are triggered via <label htmlFor>, not programmatic
    // input.click(): iOS Safari silently ignores synthetic clicks on
    // display:none file inputs in some contexts, leaving the control dead on
    // iPhones (same fix as ImageSlot). The inputs are sr-only, not hidden.
    const fileInputId = useId();
    const featuredInputId = useId();
    const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
    const [expanded, setExpanded] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isFeaturedDragging, setIsFeaturedDragging] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

    const [uploadImages, { isLoading: isUploadingInternal }] = useUploadAssetImagesMutation();
    const [deleteImage, { isLoading: isDeleting }] = useDeleteAssetImageMutation();
    const [replaceFeaturedImage, { isLoading: isReplacingFeaturedInternal }] = useReplaceFeaturedImageMutation();

    const isEditing = mode === 'edit';
    // Deferred mode: parent owns image state and will call mutations on Save
    const isDeferred = isEditing && Boolean(onFeaturedImageSelect);

    // Effective loading states — from parent props in deferred mode, from mutations otherwise
    const isUploading = isDeferred ? (isUploadingImages ?? false) : isUploadingInternal;
    const isReplacingFeatured = isDeferred ? (isReplacingFeaturedExternal ?? false) : isReplacingFeaturedInternal;

    // Stable preview URLs for pending files (recomputed only when the array reference changes)
    const pendingFeaturedPreview = useMemo(
        () => (pendingFeaturedImage ? URL.createObjectURL(pendingFeaturedImage) : null),
        [pendingFeaturedImage],
    );
    useEffect(() => {
        return () => {
            if (pendingFeaturedPreview) URL.revokeObjectURL(pendingFeaturedPreview);
        };
    }, [pendingFeaturedPreview]);

    const pendingAdditionalPreviews = useMemo(
        () => (pendingAdditionalImages ?? []).map((f, i) => ({ url: URL.createObjectURL(f), index: i })),
        [pendingAdditionalImages],
    );
    useEffect(() => {
        return () => {
            pendingAdditionalPreviews.forEach((p) => URL.revokeObjectURL(p.url));
        };
    }, [pendingAdditionalPreviews]);
    const pendingPreviewUrlSet = useMemo(
        () => new Set(pendingAdditionalPreviews.map((p) => p.url)),
        [pendingAdditionalPreviews],
    );

    // In deferred mode, display the pending state; otherwise display server state directly
    const effectiveFeaturedImage = isDeferred ? (pendingFeaturedPreview ?? featuredImage) : featuredImage;
    const effectiveAdditionalImages = isDeferred
        ? [
            ...additionalImages.filter((url) => !(pendingDeletions ?? []).includes(url)),
            ...pendingAdditionalPreviews.map((p) => p.url),
        ]
        : additionalImages;

    const validFeatured = effectiveFeaturedImage && !failedUrls.has(effectiveFeaturedImage) ? effectiveFeaturedImage : null;
    const validExtra = effectiveAdditionalImages.filter((url) => !failedUrls.has(url) && url !== validFeatured);
    const allValid = [...(validFeatured ? [validFeatured] : []), ...validExtra];

    const isLimited = maxImages && !expanded && !isEditing && allValid.length > maxImages;
    const displayImages = isLimited ? allValid.slice(0, maxImages) : allValid;
    const hiddenCount = allValid.length - (maxImages ?? 0);

    const handleImageError = (url: string) => {
        setFailedUrls((prev) => new Set(prev).add(url));
    };

    // Mirrors the server's multer cap so a phone photo over the limit fails
    // with a named reason instead of a generic request error.
    const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
    const splitOversized = (files: FileList | File[]): { ok: File[], tooLarge: File[] } => {
        const all = Array.from(files);
        return {
            ok: all.filter((f) => f.size <= MAX_UPLOAD_BYTES),
            tooLarge: all.filter((f) => f.size > MAX_UPLOAD_BYTES),
        };
    };

    const handleUpload = async (allFiles: FileList | File[]) => {
        if (allFiles.length === 0) return;
        setUploadError(null);
        const { ok: files, tooLarge } = splitOversized(allFiles);
        if (tooLarge.length > 0) {
            setUploadError(t('asset_detail.images.too_large', {
                files: tooLarge.map((f) => f.name).join(', '),
            }));
        }
        if (files.length === 0) return;
        if (isDeferred) {
            onAdditionalImagesAdd?.(files);
            return;
        }
        if (!assetId) return;
        try {
            const result = await uploadImages({ assetId, files }).unwrap();
            if (result.failedFiles?.length > 0) {
                setUploadError(result.failedFiles.map((f) => `${f.filename}: ${f.error}`).join(', '));
            }
        } catch (err: unknown) {
            const error = err as { data?: { message?: string }, message?: string };
            setUploadError(error?.data?.message ?? error?.message ?? 'Upload failed');
        }
    };

    const handleDelete = async (imageUrl: string) => {
        if (isDeferred) {
            // If deleting a pending (not-yet-uploaded) image, remove it from the pending list
            if (pendingPreviewUrlSet.has(imageUrl)) {
                const pending = pendingAdditionalPreviews.find((p) => p.url === imageUrl);
                if (pending) onPendingAdditionalDelete?.(pending.index);
            } else {
                // Mark an existing server image for deletion on save
                onImageDelete?.(imageUrl);
            }
            return;
        }
        if (!assetId) return;
        setUploadError(null);
        try {
            await deleteImage({ assetId, imageUrl }).unwrap();
        } catch (err: unknown) {
            const error = err as { data?: { message?: string }, message?: string };
            setUploadError(error?.data?.message ?? error?.message ?? 'Delete failed');
        }
    };

    const handleReplaceFeatured = async (file: File) => {
        setUploadError(null);
        if (file.size > MAX_UPLOAD_BYTES) {
            setUploadError(t('asset_detail.images.too_large', { files: file.name }));
            return;
        }
        if (isDeferred) {
            onFeaturedImageSelect?.(file);
            return;
        }
        if (!assetId) return;
        try {
            await replaceFeaturedImage({ assetId, file }).unwrap();
        } catch (err: unknown) {
            const error = err as { data?: { message?: string }, message?: string };
            setUploadError(error?.data?.message ?? error?.message ?? 'Upload failed');
        }
    };

    const handleFeaturedFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleReplaceFeatured(e.target.files[0]);
            e.target.value = '';
        }
    };

    const handleFeaturedDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsFeaturedDragging(false);
        const imageFile = Array.from(e.dataTransfer.files ?? []).find((f) => f.type.startsWith('image/'));
        if (imageFile) {
            void handleReplaceFeatured(imageFile);
        } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setUploadError(t('asset_detail.images.invalid_file_type'));
        }
    };
    const handleFeaturedDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsFeaturedDragging(true);
    };
    const handleFeaturedDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsFeaturedDragging(false);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            handleUpload(e.target.files);
            e.target.value = '';
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files) {
            const imageFiles = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
            if (imageFiles.length === 0) {
                setUploadError(t('asset_detail.images.invalid_file_type'));
                return;
            }
            handleUpload(imageFiles);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const navigateLightbox = useCallback((direction: 'prev' | 'next') => {
        setLightboxUrl((prev) => {
            if (!prev) return prev;
            const idx = allValid.indexOf(prev);
            if (idx === -1) return prev;
            const newIdx = direction === 'next'
                ? (idx + 1) % allValid.length
                : (idx - 1 + allValid.length) % allValid.length;
            return allValid[newIdx];
        });
    }, [allValid]);

    const handleLightboxKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Escape') setLightboxUrl(null);
        if (e.key === 'ArrowRight') navigateLightbox('next');
        if (e.key === 'ArrowLeft') navigateLightbox('prev');
    }, [navigateLightbox]);

    const hasAnyImages = allValid.length > 0;
    const showsCoverMissing = showsCoverMissingTile(coverMissing, isEditing, validFeatured);

    if (!isEditing && !hasAnyImages && !showsCoverMissing) {
        return (
            <AssetDetailSection title={t('asset_detail.sections.images')}>
                <p className={'text-sm text-foreground/40 italic'}>
                    {t('asset_detail.empty_states.images')}
                </p>
            </AssetDetailSection>
        );
    }

    return (
        <AssetDetailSection title={t('asset_detail.sections.images')}>
            {uploadError && (
                <div className={'mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-800'}>
                    {uploadError}
                </div>
            )}

            <div className={'max-h-[500px] overflow-y-auto pr-1'}>
                <div className={'grid grid-cols-2 gap-2'}>
                    {isEditing && !validFeatured && (
                        <label
                            htmlFor={featuredInputId}
                            className={
                                'relative aspect-square overflow-hidden rounded-lg cursor-pointer'
                                + ' border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors'
                                + (isFeaturedDragging
                                    ? ' border-primary bg-primary/5'
                                    : ' border-foreground/20 hover:border-foreground/40')
                            }
                            onDrop={handleFeaturedDrop}
                            onDragOver={handleFeaturedDragOver}
                            onDragLeave={handleFeaturedDragLeave}
                        >
                            {isReplacingFeatured ? (
                                <div className={'animate-spin rounded-full h-5 w-5 border-b-2 border-primary'} />
                            ) : (
                                <>
                                    <span className={'text-2xl text-foreground/30'}>+</span>
                                    <span className={'text-xs text-foreground/40'}>{t('asset_detail.images.add_featured')}</span>
                                </>
                            )}
                        </label>
                    )}
                    <CoverMissingTile show={showsCoverMissing} />
                    {displayImages.map((url, i) => {
                        const isFeatured = url === validFeatured;
                        const allowFeaturedDrop = isFeatured && isEditing;
                        return (
                            <div
                                key={url}
                                className={`relative group aspect-square overflow-hidden rounded-lg cursor-pointer transition-all ${
                                    allowFeaturedDrop && isFeaturedDragging ? 'ring-2 ring-primary ring-offset-2' : ''
                                }`}
                                onClick={() => setLightboxUrl(url)}
                                onDrop={allowFeaturedDrop ? handleFeaturedDrop : undefined}
                                onDragOver={allowFeaturedDrop ? handleFeaturedDragOver : undefined}
                                onDragLeave={allowFeaturedDrop ? handleFeaturedDragLeave : undefined}
                            >
                                <img
                                    src={url}
                                    alt={`${t('asset_detail.sections.images')} ${i + 1}`}
                                    className={'w-full h-full object-cover'}
                                    onError={() => handleImageError(url)}
                                />
                                {isFeatured && (
                                    <>
                                        <span className={'absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded'}>
                                            {t('asset_detail.fields.featured_image')}
                                        </span>
                                        {isEditing && (
                                            <label
                                                htmlFor={featuredInputId}
                                                onClick={(e) => e.stopPropagation()}
                                                className={'absolute top-2 right-2 bg-primary text-white cursor-pointer'
                                                    + ' rounded-full w-7 h-7 flex items-center justify-center text-xs'
                                                    + ' transition-opacity shadow-md '
                                                    // Hover-revealed on pointer devices, always visible on
                                                    // touch (below lg): iOS has no hover, so hidden-until-
                                                    // hover controls are unusable there.
                                                    + (isReplacingFeatured
                                                        ? 'pointer-events-none opacity-50'
                                                        : 'opacity-100 lg:opacity-0 lg:group-hover:opacity-100')}
                                                title={t('asset_detail.images.replace_featured')}
                                            >
                                                {isReplacingFeatured ? (
                                                    <span className={'animate-spin rounded-full h-3 w-3 border-b-2 border-white'} />
                                                ) : (
                                                    '\u270E'
                                                )}
                                            </label>
                                        )}
                                    </>
                                )}
                                {isEditing && !isFeatured && (
                                    <button
                                        type={'button'}
                                        onClick={(e) => { e.stopPropagation(); handleDelete(url); }}
                                        disabled={isDeleting}
                                        className={'absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white'
                                            + ' rounded-full w-7 h-7 flex items-center justify-center text-sm'
                                            + ' opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity disabled:opacity-50'
                                            + ' shadow-md'}
                                        title={t('asset_detail.actions.remove')}
                                    >
                                        &times;
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {!isEditing && isLimited && (
                <button
                    type={'button'}
                    onClick={() => setExpanded(true)}
                    className={'mt-2 text-sm text-foreground/60 hover:text-foreground'}
                >
                    {t('asset_detail.show_all_images', { count: hiddenCount })}
                </button>
            )}
            {!isEditing && expanded && maxImages && (
                <button
                    type={'button'}
                    onClick={() => setExpanded(false)}
                    className={'mt-2 text-sm text-foreground/60 hover:text-foreground'}
                >
                    {t('asset_detail.show_fewer_images')}
                </button>
            )}

            {/* Upload area — edit mode only */}
            {isEditing && (
                <>
                    <label
                        htmlFor={fileInputId}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        className={`block mt-3 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                            isDragging
                                ? 'border-primary bg-primary/5'
                                : 'border-foreground/20 hover:border-foreground/40'
                        }`}
                    >
                        {isUploading ? (
                            <div className={'flex items-center justify-center gap-2'}>
                                <div className={'animate-spin rounded-full h-5 w-5 border-b-2 border-primary'} />
                                <span className={'text-sm text-foreground/60'}>{t('asset_detail.images.uploading')}</span>
                            </div>
                        ) : (
                            <>
                                <p className={'text-sm text-foreground/60'}>
                                    {t('asset_detail.images.drop_or_click')}
                                </p>
                                <p className={'text-xs text-foreground/40 mt-1'}>
                                    {t('asset_detail.images.max_size')}
                                </p>
                            </>
                        )}
                    </label>
                    <input
                        id={fileInputId}
                        type={'file'}
                        accept={'image/*'}
                        multiple
                        onChange={handleFileSelect}
                        className={'sr-only'}
                    />
                    <input
                        id={featuredInputId}
                        type={'file'}
                        accept={'image/*'}
                        onChange={handleFeaturedFileSelect}
                        className={'sr-only'}
                    />
                </>
            )}

            {/* Lightbox */}
            {lightboxUrl && (
                <div
                    className={'fixed inset-0 z-[1300] bg-black/80 flex items-center justify-center'}
                    onClick={() => setLightboxUrl(null)}
                    onKeyDown={handleLightboxKeyDown}
                    tabIndex={0}
                    ref={(el) => el?.focus()}
                >
                    {/* Close button */}
                    <button
                        type={'button'}
                        onClick={() => setLightboxUrl(null)}
                        className={'absolute top-4 right-4 text-white text-3xl hover:text-white/80 z-10'}
                    >
                        &times;
                    </button>

                    {/* Previous */}
                    {allValid.length > 1 && (
                        <button
                            type={'button'}
                            onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
                            className={'absolute left-4 text-white text-4xl hover:text-white/80 z-10 select-none'}
                        >
                            &#8249;
                        </button>
                    )}

                    {/* Image */}
                    <img
                        src={lightboxUrl}
                        alt={''}
                        className={'max-h-[90vh] max-w-[90vw] object-contain rounded-lg'}
                        onClick={(e) => e.stopPropagation()}
                    />

                    {/* Next */}
                    {allValid.length > 1 && (
                        <button
                            type={'button'}
                            onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
                            className={'absolute right-4 text-white text-4xl hover:text-white/80 z-10 select-none'}
                        >
                            &#8250;
                        </button>
                    )}

                    {/* Counter */}
                    <span className={'absolute bottom-4 text-white/60 text-sm'}>
                        {allValid.indexOf(lightboxUrl) + 1} / {allValid.length}
                    </span>
                </div>
            )}
        </AssetDetailSection>
    );
};

export default ImagesSection;
